/**
 * productEvents.js — North Star product events from the React app.
 *
 * Feeds Weekly Landed Drafts (WLD). Separate from analytics.js on purpose:
 * track() is a GA4/Meta/UET funnel relay with vendor fan-out semantics, whereas
 * this is the write path for the metric the business steers by. Entangling them
 * would mean a vendor's allowlist could silently drop a landed draft.
 *
 * TWO TRANSPORTS, one deliberate asymmetry:
 *
 *   Extension → relay through the service worker (PA_TELEMETRY), which calls
 *     extension/telemetry.js. That module is then the ONLY thing in the whole
 *     extension that mints or reads the client_id, so the panel (localStorage)
 *     and the worker (chrome.storage) can never diverge into two actor ids for
 *     one browser profile — which would inflate every per-actor denominator.
 *     It also gets the offline queue and the consent gate for free.
 *
 *   Web → POST directly; there is no service worker, and no Gmail to land in.
 *
 * Neither path sends an auth token. user_id is attached server-side on
 * draft_generated (which the backend emits itself, holding a verified JWT), and
 * client_identity carries the client_id → user_id link forward from there. The
 * funnel counts DISTINCT client_id anyway — user_id is only for revenue joins.
 */
const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
const API_BASE = env.VITE_API_BASE || '';

const isExtension = () => typeof window !== 'undefined' && !!window.__AIMAIL_IS_EXTENSION;

function webClientId() {
  // Mirrors analytics.js getClientId() — same localStorage key, so the web app
  // reports one actor id across both systems.
  try {
    let id = localStorage.getItem('persona_client_id');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
      localStorage.setItem('persona_client_id', id);
    }
    return id;
  } catch { return 'anon'; }
}

/**
 * Emit one product event. Fire-and-forget; never throws, never blocks.
 *
 * @param {string} event  install | panel_open | draft_generated | draft_landed
 *                        | draft_land_failed | draft_copied
 * @param {object} props  { surface, draft_uid, ok, reason, extra }
 */
export function emitProductEvent(event, props = {}) {
  try {
    if (isExtension()) {
      if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) return;
      chrome.runtime.sendMessage({ type: 'PA_TELEMETRY', event, props },
        () => { void chrome.runtime.lastError; });
      return;
    }
    fetch(`${API_BASE}/api/v1/pe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        client_id: webClientId(),
        events: [{
          id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()),
          event,
          ts: Date.now(),
          surface: props.surface || 'web',
          draft_uid: props.draft_uid || null,
          ok: typeof props.ok === 'boolean' ? props.ok : null,
          reason: props.reason || null,
          props: props.extra || {},
        }],
      }),
    }).catch(() => {});
  } catch { /* telemetry must never surface to the user */ }
}

/**
 * Release the install event queued at chrome.runtime.onInstalled.
 *
 * Call right after the first-run data-use disclosure is acked. Until then
 * telemetry.js holds everything on-device — the install is flushed with its
 * ORIGINAL timestamp, so waiting for consent costs no cohort accuracy.
 */
export function flushProductEvents() {
  try {
    if (!isExtension() || typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) return;
    chrome.runtime.sendMessage({ type: 'PA_TELEMETRY_FLUSH' }, () => { void chrome.runtime.lastError; });
  } catch { /* ignore */ }
}

let _clientId = null;

/**
 * Resolve this browser's actor id, for attaching to the generate request so the
 * backend can emit draft_generated in the same identity space as the landing.
 *
 * Async because in the extension the canonical copy lives in chrome.storage —
 * the service worker mints it at install time and cannot see localStorage. Both
 * sides get-or-create the SAME key, so they converge on one id; if they ever
 * didn't, one browser profile would count as two installs.
 */
export function resolveClientId() {
  if (_clientId) return Promise.resolve(_clientId);
  if (!isExtension()) {
    _clientId = webClientId();
    return Promise.resolve(_clientId);
  }
  return new Promise((resolve) => {
    try {
      chrome.storage.local.get('pa_client_id', (d) => {
        let id = d && d.pa_client_id;
        if (!id) {
          id = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
          try { chrome.storage.local.set({ pa_client_id: id }, () => { void chrome.runtime.lastError; }); } catch { /* ignore */ }
        }
        _clientId = id;
        resolve(id);
      });
    } catch { resolve(null); }
  });
}

/**
 * Mint an id for one generated draft.
 *
 * WLD counts DISTINCT drafts, not insert clicks: a toolbar auto-draft inserts
 * itself and the user can then hit Insert again on the same text, which is two
 * insert events but one landed draft. Carrying this id from generation through
 * to the insert is what keeps the metric meaning what everyone assumes it means.
 */
export function newDraftUid() {
  try {
    return (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
  } catch { return String(Date.now()); }
}
